import React, { useState, useEffect } from "react";
import {useTranslation} from "react-i18next";

import Typography from "@mui/material/Typography";

import {useLazyGetDeclarationsQuery} from "../../../../../../../../../api/dictionary";
import Preloader from "../../../../../../../../common/preloader";
import DeclarationsExamplesListItem
  from "../../../../../../../../common/declarationsExamplesListItem";
import {COLORS} from "../../../../../../../../../config/constants";
import {formatReferenceCopy as formatDemoCopy} from "utils/typography";

import './declarations-list.scss';

type Props = {
    code?: string,
}

const DeclarationsList = ({code} : Props) => {

  const { t, i18n } = useTranslation()

  const [ examples, setExamples ] = useState<DeclarationsOutput[]>([]);
  const [ notFoundError, setNotFoundError ] = useState<boolean | string>(false);
  const [ isLoading, setIsLoading ] = useState<boolean | string>(false);

  const [ offset, setOffset ] = useState(0);
  const [ hasMore, setHasMore ] = useState(true);
  const [ currentCode, setCurrentCode ] = useState('');

  const [ declarationsRequest ] = useLazyGetDeclarationsQuery()

  const loadExamples = async (code: string, newOffset = 0) => {

    const codeWithoutSpaces = code.replace(/\s+/g, "");

    if (codeWithoutSpaces.length !== 10) return;

    setIsLoading(true);
    setNotFoundError(false);

    try {
      const resp = await declarationsRequest({
        code: codeWithoutSpaces,
        limit: 20,
        offset: newOffset
      }).unwrap();

      if (newOffset === 0) {
        setExamples(resp);
        if (!resp.length){
          setTimeout(() => {
            loadExamples(code)
          }, 5000);
        }
      } else {
        setExamples([ ...examples, ...resp ]);
      }

      if (!resp.length && offset === 0) {
        setExamples([]);
        setNotFoundError(true);
        return;
      }

      setHasMore(resp.length === 20);
      setOffset(newOffset);

    } catch (error) {
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  const loadMore = (code: string) => {
    if (isLoading || !hasMore) return;
    loadExamples(code, offset + 20);
  };

  useEffect(() => {
    if (code) {
      loadExamples(code);
      setCurrentCode(code);
    }
  }, [ code ])

  const hasExamples = Boolean(examples?.length);

  return <>
    {hasExamples && (
      <div className='customs-declaration-examples' lang={i18n.resolvedLanguage}>
        {examples?.map((example, index) => (
          <DeclarationsExamplesListItem
            key={index}
            code={example.code}
            title={example.title}
          />
        ))}
        {hasMore && !isLoading && <Typography variant='caption'
          sx={{
            color: COLORS.BLUE,
            marginLeft: '104px',
            letterSpacing: 0,
          }}
          onClick={() => loadMore(currentCode)}
          className='customs-declaration-examples__show-more'>
          {formatDemoCopy(t('calc__text__main__showMoreExamples'))}
        </Typography>}
        {(isLoading || notFoundError) && <div className='customs-declaration-examples__preloader'><Preloader/></div>}
      </div>
    )}
  </>

}

export default DeclarationsList

